import React, { useEffect, useRef, useState, useMemo } from "react";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";

export default function DetallesView({
  fechaOrden,
  clienteId,
  clientes = [],
  productos = [],
  descuento = 0,
  pedidoNumero,
  numeroOrden,
  ordenId,
  folio,
  estado: estadoProp,
  divisa = "MXN",
  ivaPct = 16,
  envio = {},
  pago = {},
  pago2 = {},
  // handlers (vienen de PreviaPedidos)
  onEditar = () => {},
  onImprimir = () => {},
  onFacturar = () => {},
  onEliminar = () => {},
  // extra visibles en Info Cliente
  atencion,
  correo,

  // ⬇️ NUEVO: se inyecta contenido al final (subtabs + contenido)
  footer = null,
}) {
  const { formatDate } = useFunctionsPrevia();
  const rootRef = useRef(null);
  const [pedidoNoDOM, setPedidoNoDOM] = useState("");
  const [live, setLive] = useState(null);

  // === Live snapshot del pedido ===
  useEffect(() => {
    if (!ordenId) return;
    const ref = doc(db, "pedidos", ordenId);
    const unsub = onSnapshot(ref, { includeMetadataChanges: true }, (snap) => {
      if (!snap.exists()) return;
      setLive(snap.data());
    });
    return () => unsub();
  }, [ordenId]);

  const toDateObj = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v?.toDate === "function") return v.toDate();
    if (typeof v === "number") return new Date(v);
    return new Date(v);
  };

  const formatFechaTitulo = (v) => {
    const d = toDateObj(v);
    if (!d || isNaN(d.getTime())) return "—";
    const parts = new Intl.DateTimeFormat("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).formatToParts(d);
    const day = parts.find((p) => p.type === "day")?.value ?? "";
    let month = parts.find((p) => p.type === "month")?.value ?? "";
    month = month.charAt(0).toUpperCase() + month.slice(1);
    const year = parts.find((p) => p.type === "year")?.value ?? "";
    return `${day} ${month} ${year}`;
  };

  // Ocultar duplicados externos
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    document.querySelectorAll("h1,h2,[role='heading']").forEach((h) => {
      const txt = (h.textContent || "").trim().toLowerCase();
      if (txt === "orden de compra") h.style.display = "none";
    });

    const numeroEsperado = [
      pedidoNumero,
      numeroOrden,
      folio,
      live?.numeroOrdenDeCompra,
      ordenId,
    ].find(Boolean);

    const selector = "h1,h2,h3,strong,b,span";
    const candidatos = Array.from(document.querySelectorAll(selector)).filter(
      (el) => {
        const pos = el.compareDocumentPosition(root);
        const esAntes = !!(pos & Node.DOCUMENT_POSITION_FOLLOWING);
        const esAncestro = !!(pos & Node.DOCUMENT_POSITION_CONTAINS);
        return esAntes && !esAncestro;
      }
    );

    const rx = numeroEsperado
      ? new RegExp(`^#\\s*${numeroEsperado}\\s*$`)
      : /^#\s*([0-9A-Za-z-]{6,})\s*$/;

    for (const el of candidatos) {
      const text = (el.textContent || "").trim();
      const match = text.match(rx);
      if (!match) continue;
      if (!numeroEsperado && match[1]) setPedidoNoDOM((p) => p || match[1]);
      el.style.display = "none";
      break;
    }
  }, [pedidoNumero, numeroOrden, folio, ordenId, live?.numeroOrdenDeCompra]);

  // ======== RESOLVER FUENTES: live > props =========
  const clienteLiveId = live?.cliente ?? clienteId;
  const clienteObj = (clientes || []).find((c) => c.id === clienteLiveId) || {};
  const nombreCliente = clienteObj?.empresa || "Cliente no encontrado";

  const atencionEff = live?.atencion ?? atencion ?? clienteObj.atencion;
  const correoEff =
    live?.correo ?? correo ?? clienteObj.correo ?? clienteObj.email;
  const envioEff = live?.envio ?? envio;

  const productosEff = live?.productosSeleccionados ?? productos;
  const descuentoEff = live?.descuento ?? descuento;
  const estadoEff = live?.estado ?? estadoProp ?? envio?.estado ?? "Pendiente";
  const fechaOrdenEff = live?.fechaOrden ?? fechaOrden;

  const pedidoNo =
    [
      pedidoNumero,
      numeroOrden,
      folio,
      live?.numeroOrdenDeCompra,
      ordenId,
      pedidoNoDOM,
    ].find(Boolean) || "—";

  // === formateadores ===
  const nf = useMemo(
    () =>
      new Intl.NumberFormat("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );
  const cfMXN = useMemo(
    () =>
      new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
      }),
    []
  );
  const moneyPrefixMXN = (n) => `${divisa} ${nf.format(Number(n || 0))}`;
  const moneySuffixMXN = (n) => `${cfMXN.format(Number(n || 0))} ${divisa}`;

  // === Estado ===
  const normalizarEstado = (v) => {
    const s = (v || "").toString().trim().toLowerCase();
    if (s === "pagado" || s === "pagada") return "Pagada";
    if (s === "facturado" || s === "facturada") return "Facturado";
    if (s === "en proceso" || s === "proceso") return "En Proceso";
    if (s === "pendiente") return "Pendiente";
    return v || "Pendiente";
  };

  const estadoResuelto = normalizarEstado(estadoEff);

  const estadoClase =
    estadoResuelto === "Pagada"
      ? "pill-pagada"
      : estadoResuelto === "En Proceso"
      ? "pill-en-proceso"
      : estadoResuelto === "Facturado"
      ? "pill-facturado"
      : "pill-pendiente";

  const safeText = (v) => {
    if (v == null || v === "") return "—";
    if (typeof v === "object") {
      const isAddress =
        "calle" in v ||
        "colonia" in v ||
        "codigoPostal" in v ||
        "estado" in v ||
        "ciudad" in v ||
        "numeroExt" in v ||
        "numeroInt" in v;
      if (isAddress) {
        const {
          calle,
          numeroExt,
          numeroInt,
          colonia,
          ciudad,
          estado,
          codigoPostal,
        } = v;
        return [
          calle,
          numeroExt ? `#${numeroExt}` : null,
          numeroInt ? `Int. ${numeroInt}` : null,
          colonia,
          ciudad,
          estado,
          codigoPostal ? `C.P. ${codigoPostal}` : null,
        ]
          .filter(Boolean)
          .join(", ");
      }
      if (Array.isArray(v)) return v.filter(Boolean).join(", ");
      return Object.values(v).filter(Boolean).join(", ");
    }
    return String(v);
  };

  // === Totales ===
  const { subtotal, descVal, iva, total } = useMemo(() => {
    const sub = (productosEff || []).reduce(
      (sum, p) => sum + Number(p.importe || 0),
      0
    );
    const dVal = sub * (Number(descuentoEff) / 100);
    const base = Math.max(sub - dVal, 0);
    const ivaCalc = base * (ivaPct / 100);
    return {
      subtotal: sub,
      descVal: dVal,
      iva: ivaCalc,
      total: base + ivaCalc,
    };
  }, [productosEff, descuentoEff, ivaPct]);

  const pagoLabel = (obj = {}) =>
    `${safeText(obj.metodo || "Tarjeta de crédito")} ${
      obj.mascara ? `${obj.mascara}` : "•••• 4242"
    }`;

  // Menú “Más”
  const [openMore, setOpenMore] = useState(false);
  const moreRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (!moreRef.current) return;
      if (!moreRef.current.contains(e.target)) setOpenMore(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpenMore(false);
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  return (
    <div ref={rootRef} className="pedido-details">
      <style>{`
        .pedido-details{
          --gutter: 24px;
          padding: 0 var(--gutter) 24px;
          display: grid;
          gap: 18px;
          box-sizing: border-box;
          overflow-x: hidden;
        }
        .pd-header{ display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
        .pd-titles{ display:flex; flex-direction:column; }
        .pd-headline{ display:flex; align-items:center; gap:10px; }
        .pd-title{ margin:0; font-size:22px; font-weight:800; color:#0f172a; }
        .pd-divider{ width:1px; height:26px; background:#E6EDFF; border-radius:1px; }

        .pd-pill{ padding:4px 10px; border-radius:6px; font-weight:700; line-height:1.1; border:1px solid transparent; }
        .pd-pill.pill-pendiente{ background: rgba(211,211,211,.3); color:#A9A9A9; border-color:#A9A9A9; }
        .pd-pill.pill-en-proceso{ background: rgba(255,255,0,.2); color:#FFB800; border-color:#FFD966; }
        .pd-pill.pill-facturado{ background:#f3e8ff; color:#a855f7; border-color:#a855f7; }
        .pd-pill.pill-pagada{ background: rgba(144,238,144,.2); color:#4CAF50; border-color:#4CAF50; }

        .pd-date{ margin-top:10px; color:#6b7280; font-size:13px; }
        .pd-date-label{ font-weight:700; margin-right:4px; }

        /* Botones outline */
        .pd-actions{ display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        .btn-outline{
          display:inline-flex; gap:6px; align-items:center; padding:8px 12px;
          background:#fff; border:1px solid #E6EDFF; border-radius:10px; cursor:pointer;
          color:#111827;
        }
        .btn-outline:hover{ background:#f8fafc; }
        .btn-outline i{ font-size:16px; }

        /* Menú Más */
        .more-wrap{ position:relative; }
        .more-menu{
          position:absolute; right:0; top:100%; margin-top:6px;
          background:#fff; border:1px solid #E6EDFF; border-radius:10px;
          min-width:190px; padding:6px; z-index:30;
          box-shadow: 0 8px 20px rgba(16,24,40,.08);
        }
        .more-item{
          display:flex; align-items:center; gap:8px;
          padding:8px 10px; border-radius:8px; cursor:pointer;
          color:#111827; text-decoration:none;
        }
        .more-item:hover{ background:#f4f8ff; }

        /* ======= GRIDS ======= */
        .pd-info-grid{
          display:grid; grid-template-columns:1fr 1fr; gap:14px;
        }
        .pd-pay-grid{ display:grid; grid-template-columns:1fr 1fr; gap:14px; }

        /* --- Rails de alineación con header/tabla --- */
        .pd-info-grid > .card:first-child{ margin-left:-2px; }
        .pd-info-grid > .card:last-child{  margin-right:-2px; }
        .pd-pay-grid  > .card:first-child{ margin-left:-2px; }
        .pd-pay-grid  > .card:last-child{  margin-right:-2px; }

        @media (max-width:1024px){
          .pd-info-grid{ grid-template-columns:1fr; }
          .pd-pay-grid{ grid-template-columns:1fr; }
          /* sin rails en una columna */
          .pd-info-grid > .card:first-child,
          .pd-info-grid > .card:last-child,
          .pd-pay-grid  > .card:first-child,
          .pd-pay-grid  > .card:last-child{
            margin-left:0; margin-right:0;
          }
        }

        .card{ background:#fff; border:1px solid #E6EDFF; border-radius:12px; padding:14px; box-shadow:none; }
        .card-title{ font-weight:700; margin-bottom:10px; color:#1f2937; }

        .dl.grid-2{ display:grid; grid-template-columns:160px 1fr; column-gap:10px; row-gap:0; }
        .dl dt{ color:#6b7280; }
        .dl dd{ margin:0; color:#111827; }
        .dl.grid-2 dt, .dl.grid-2 dd{ padding:10px 0; border-bottom:1px solid #E6EDFF; }
        .dl.grid-2 dt:nth-last-child(2), .dl.grid-2 dd:last-child{ border-bottom:0; }

        .card-row{ display:flex; align-items:center; justify-content:space-between; gap:10px; }

        .pd-table-card{
          --col-qty: 120px; --col-price: 140px; --col-disc: 140px; --col-sub: 160px;
          background:#fff; border:1px solid #E6EDFF; border-radius:12px; padding:16px;
        }
        .section-title{ font-weight:700; color:#111827; margin-bottom:12px; }

        .pd-table{
          border:1px solid #E6EDFF;
          border-radius:8px;
          overflow:hidden;
        }
        .pd-table-head,
        .pd-table-row{
          display:grid;
          grid-template-columns: 1fr var(--col-qty) var(--col-price) var(--col-disc) var(--col-sub);
          gap:10px;
          padding:12px;
          align-items:center;
        }
        .pd-table-head{
          background:#f8fafc;
          color:#111827;
          font-weight:800;
          text-transform:uppercase;
          font-size:12px;
          border-bottom:1px solid #E6EDFF; /* sólo línea inferior */
        }
        .pd-table-head > div{ text-align:center; }
        .pd-table-head > div:first-child{ text-align:left; }
        .pd-table-row{
          background:#fff;
          border-bottom:1px solid #edf2f7; /* separador entre filas */
        }
        .pd-table-row:last-child{
          border-bottom:0; /* el borde inferior lo pone .pd-table */
        }
        .pd-table-row .desc{ color:#0f172a; text-align:left; }
        .num{ text-align:center; white-space:nowrap; }

        .pd-totals{
          margin-top:16px;
          display:grid;
          row-gap:8px;
          border-top: 1px solid #E6EDFF;
          padding-top: 10px;
        }
        .pd-totals .line{
          display:grid;
          grid-template-columns: 1fr var(--col-qty) var(--col-price) var(--col-disc) var(--col-sub);
          align-items:center; column-gap:10px; padding:0 12px;
        }
        .pd-totals .label{ grid-column: 1 / 5; text-align:left; }
        .pd-totals .value{ grid-column: 5 / 6; justify-self: center; text-align:center; min-width: var(--col-sub); font-variant-numeric: tabular-nums; }
        .pd-totals .line.total{ border-top:1px solid #e5e7eb; padding-top:10px; margin-top:6px; }
        .pd-totals .line.total .label, .pd-totals .line.total .value{ font-weight:800; }

        .green{ color:#0f9d58; }
        .bold{ font-weight:800; }

        /* pequeño margen para el slot de tabs inferiores */
        .pd-footer-slot{ margin-top:16px; }
      `}</style>

      {/* HEADER + BOTONES OUTLINE */}
      <div className="pd-header">
        <div className="pd-titles">
          <div className="pd-headline">
            <h2 className="pd-title">Pedido #{pedidoNo}</h2>
            <span className="pd-divider" aria-hidden="true" />
            <span className={`pd-pill ${estadoClase}`}>{estadoResuelto}</span>
          </div>
          <div className="pd-date">
            <span className="pd-date-label">Fecha:</span>
            <span>{formatFechaTitulo(fechaOrdenEff)}</span>
          </div>
        </div>

        <div className="pd-actions">
          <button className="btn-outline" onClick={onEditar} title="Editar">
            <i className="bi bi-pencil-square" />
            <span>Editar</span>
          </button>

          <button className="btn-outline" onClick={onImprimir} title="Imprimir">
            <i className="bi bi-printer" />
            <span>Imprimir</span>
          </button>

          <button
            className="btn-outline"
            title="Facturar"
            disabled
            aria-disabled="true"
          >
            <i className="bi bi-receipt" />
            <span>Facturar</span>
          </button>

          <div className="more-wrap" ref={moreRef}>
            <button
              className="btn-outline"
              onClick={() => setOpenMore((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={openMore ? "true" : "false"}
              title="Más"
            >
              <i className="bi bi-list" />
              <span>Más</span>
            </button>

            {openMore && (
              <div className="more-menu" role="menu">
                <div
                  className="more-item"
                  role="menuitem"
                  onClick={() => {
                    setOpenMore(false);
                    onFacturar(); // ← usa el handler existente que abre el modal
                  }}
                >
                  <i className="bi bi-journal-text" />
                  Asociar factura
                </div>

                <div
                  className="more-item"
                  role="menuitem"
                  onClick={() => {
                    setOpenMore(false);
                    onEliminar();
                  }}
                >
                  <i className="bi bi-trash3" />
                  Eliminar pedido
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TARJETAS */}
      <div className="pd-info-grid">
        <div className="card">
          <div className="card-title">Información Cliente</div>
          <dl className="dl grid-2">
            <dt>Nombre</dt>
            <dd>{safeText(nombreCliente)}</dd>
            <dt>Atención</dt>
            <dd>{safeText(atencionEff)}</dd>
            <dt>Teléfono</dt>
            <dd>{safeText(clienteObj.telefono)}</dd>
            <dt>Correo</dt>
            <dd>{safeText(correoEff)}</dd>
            <dt>Dirección</dt>
            <dd>{safeText(clienteObj.direccion)}</dd>
          </dl>
        </div>

        <div className="card">
          <div className="card-title">Detalles del Envío</div>
          <dl className="dl grid-2">
            <dt>Método de envío</dt>
            <dd>{safeText(envioEff?.metodo)}</dd>
            <dt>Paquetería</dt>
            <dd>{safeText(envioEff?.paqueteria)}</dd>
            <dt>Fecha de envío</dt>
            <dd>
              {envioEff?.fechaEnvio ? formatDate(envioEff?.fechaEnvio) : "—"}
            </dd>
            <dt>Número de guía</dt>
            <dd>{safeText(envioEff?.numeroGuia)}</dd>
            <dt>Dirección</dt>
            <dd>{safeText(envioEff?.direccion)}</dd>
          </dl>
        </div>
      </div>

      {/* MÉTODOS DE PAGO */}
      <div className="pd-pay-grid">
        <div className="card">
          <div className="card-row">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: 700,
              }}
            >
              <i className="bi bi-credit-card-2-front" /> Método de pago
            </div>
            <div>{pagoLabel(pago)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-row">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight: 700,
              }}
            >
              <i className="bi bi-credit-card-2-front" /> Método de pago
            </div>
            <div>
              {pagoLabel(Object.keys(pago2 || {}).length ? pago2 : pago)}
            </div>
          </div>
        </div>
      </div>

      {/* DETALLES */}
      <div className="pd-table-card">
        <div className="section-title">Detalles</div>

        <div className="pd-table">
          <div className="pd-table-head">
            <div>DESCRIPCIÓN</div>
            <div className="num">CANTIDAD</div>
            <div className="num">PRECIO</div>
            <div className="num">DESCUENTO</div>
            <div className="num">SUBTOTAL</div>
          </div>

          {(productosEff || []).map((p, idx) => {
            const qty = Number(p.cantidad || 0);
            const price = Number(p.tarifa || 0);
            const line = Number(p.importe || qty * price);
            const dPct = Number(p.descuentoPct || p.descuento || 0);
            const dVal = Number(p.descuentoValor || 0);
            const dShown = dVal > 0 ? moneyPrefixMXN(dVal) : `${dPct || 0}%`;
            return (
              <div className="pd-table-row" key={p.id || idx}>
                <div className="desc">
                  {safeText(p.nombre || p.descripcion)}
                </div>
                <div className="num">{qty}</div>
                <div className="num">{moneyPrefixMXN(price)}</div>
                <div className="num">{dShown}</div>
                <div className="num">{moneyPrefixMXN(line)}</div>
              </div>
            );
          })}
        </div>

        {/* TOTALES */}
        <div className="pd-totals">
          <div className="line">
            <span className="label">Subtotal</span>
            <span className="value">{moneySuffixMXN(subtotal)}</span>
          </div>
          <div className="line">
            <span className="label">Descuento</span>
            <span className="value green">{moneySuffixMXN(descVal)}</span>
          </div>
          <div className="line">
            <span className="label">IVA ({ivaPct}%)</span>
            <span className="value">{moneySuffixMXN(iva)}</span>
          </div>
          <div className="line total">
            <span className="label">Total</span>
            <span className="value bold">{moneySuffixMXN(total)}</span>
          </div>
        </div>
      </div>

      {/* Slot para tabs inferiores */}
      {footer ? <div className="pd-footer-slot">{footer}</div> : null}
    </div>
  );
}
